import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { AdminCreatorPanel } from "@/pages/Profile";

const STATUS_LABEL = {
    draft: "Draft",
    draft_pending: "Draft",
    draft_ready: "Draft",
    approved: "Approved",
    held: "Held",
    released: "Released",
};

// Human time elapsed between an ISO timestamp and now. Mobile-friendly.
function waitingFor(iso) {
    if (!iso) return "";
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const mins = Math.max(0, Math.round((now - then) / 60000));
        if (mins < 60) return `${mins}m`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h`;
        const days = Math.round(hrs / 24);
        return `${days}d`;
    } catch {
        return "";
    }
}

// Admin-only tile for the "Guides" workflow. Lists existing city / category
// guide pages, lets staff create one, suggest candidates from the DB and tick
// which to include with an editable one-line description, and publish or
// unpublish. Collapsed by default.
export function GuidesAdminTile({ testid = "admin-guides-tile", openByDefault = false }) {
    const { request } = useApi();
    const [open, setOpen] = useState(openByDefault);
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        slug: "",
        title: "",
        heading: "",
        city: "",
        niche: "",
        category: "",
        intro: "",
    });
    const [editId, setEditId] = useState(null);
    const [bulkRunning, setBulkRunning] = useState(false);

    const load = async () => {
        try {
            const res = await request("/admin/guides");
            setItems(res.items || []);
            setBulkRunning(Boolean(res.bulk_lookup_running));
        } catch (err) {
            toast.error(err?.message || "Could not load guides.");
            setItems([]);
        }
    };

    useEffect(() => {
        if (open && items === null) void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, items]);

    // Poll while the bulk lookup works through the guides.
    useEffect(() => {
        if (!open || !bulkRunning) return undefined;
        const t = setInterval(() => void load(), 10000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, bulkRunning]);

    const lookupAll = async () => {
        if (busy || bulkRunning) return;
        const due = (items || []).filter(
            (g) =>
                g.guide_type !== "topic" &&
                g.city &&
                (!g.lookup_at ||
                    Date.now() - new Date(g.lookup_at).getTime() >=
                        90 * 24 * 3600 * 1000),
        ).length;
        if (due === 0) {
            toast("Every creator guide already has a current lookup.");
            return;
        }
        if (
            !window.confirm(
                `Run the lookup on ${due} guide${due === 1 ? "" : "s"}? They run one at a time and each takes a few minutes.`,
            )
        )
            return;
        setBusy(true);
        try {
            const res = await request("/admin/guides/lookup-all", {
                method: "POST",
            });
            toast(`Lookup started for ${res.queued} guides.`);
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not start the lookups.");
        } finally {
            setBusy(false);
        }
    };

    const create = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await request("/admin/guides", {
                method: "POST",
                body: form,
            });
            toast.success("Guide created.");
            setCreating(false);
            setForm({
                slug: "",
                title: "",
                heading: "",
                city: "",
                niche: "",
                category: "",
                intro: "",
            });
            setItems((prev) => [{ ...res.guide }, ...(prev || [])]);
            setEditId(res.guide?.id || null);
        } catch (err) {
            toast.error(err?.message || "Could not create guide.");
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <section
                data-testid={testid}
                className="samoony-card mb-4"
            >
                <button
                    data-testid={`${testid}-toggle`}
                    onClick={() => setOpen(true)}
                    className="samoony-pill samoony-pill-ghost w-full sm:w-auto"
                >
                    Open guides
                </button>
            </section>
        );
    }

    return (
        <section data-testid={testid} className="samoony-card mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="samoony-label">Guides</div>
                <div className="grid grid-cols-1 sm:flex gap-2">
                    <button
                        data-testid={`${testid}-import-starter`}
                        onClick={async () => {
                            if (busy) return;
                            setBusy(true);
                            try {
                                const res = await request(
                                    "/admin/guides/import-starter",
                                    { method: "POST", body: {} },
                                );
                                const created = (res.created || []).length;
                                const skipped = (res.skipped || []).length;
                                toast.success(
                                    `Starter guides: ${created} created, ${skipped} already present.`,
                                );
                                setItems(null);
                            } catch (err) {
                                toast.error(
                                    err?.message ||
                                        "Could not import starter guides.",
                                );
                            } finally {
                                setBusy(false);
                            }
                        }}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        Import starter guides
                    </button>
                    <button
                        data-testid={`${testid}-lookup-all`}
                        onClick={lookupAll}
                        disabled={busy || bulkRunning || items === null}
                        className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                    >
                        {bulkRunning ? "Looking up..." : "Run lookup on all"}
                    </button>
                    <button
                        data-testid={`${testid}-create-toggle`}
                        onClick={() => setCreating((v) => !v)}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        {creating ? "Cancel" : "Create guide"}
                    </button>
                    <button
                        data-testid={`${testid}-toggle`}
                        onClick={() => setOpen(false)}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        Hide
                    </button>
                </div>
            </div>

            {creating && (
                <div
                    data-testid={`${testid}-create-form`}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4"
                >
                    <input
                        data-testid={`${testid}-input-slug`}
                        value={form.slug}
                        onChange={(e) =>
                            setForm({ ...form, slug: e.target.value })
                        }
                        placeholder="slug e.g. calgary-food-creators"
                        className="samoony-input"
                    />
                    <input
                        data-testid={`${testid}-input-title`}
                        value={form.title}
                        onChange={(e) =>
                            setForm({ ...form, title: e.target.value })
                        }
                        placeholder="title (SEO)"
                        className="samoony-input"
                    />
                    <input
                        data-testid={`${testid}-input-heading`}
                        value={form.heading}
                        onChange={(e) =>
                            setForm({ ...form, heading: e.target.value })
                        }
                        placeholder="heading (H1) — defaults to title"
                        className="samoony-input"
                    />
                    <input
                        data-testid={`${testid}-input-city`}
                        value={form.city}
                        onChange={(e) =>
                            setForm({ ...form, city: e.target.value })
                        }
                        placeholder="city e.g. Calgary"
                        className="samoony-input"
                    />
                    <input
                        data-testid={`${testid}-input-niche`}
                        value={form.niche}
                        onChange={(e) =>
                            setForm({ ...form, niche: e.target.value })
                        }
                        placeholder="niche e.g. food & drink"
                        className="samoony-input"
                    />
                    <input
                        data-testid={`${testid}-input-category`}
                        value={form.category}
                        onChange={(e) =>
                            setForm({ ...form, category: e.target.value })
                        }
                        placeholder="category (optional) e.g. all-you-can-eat"
                        className="samoony-input"
                    />
                    <textarea
                        data-testid={`${testid}-input-intro`}
                        value={form.intro}
                        onChange={(e) =>
                            setForm({ ...form, intro: e.target.value })
                        }
                        placeholder="2 to 3 sentence intro"
                        className="samoony-input sm:col-span-2"
                        rows={3}
                    />
                    <button
                        data-testid={`${testid}-create-submit`}
                        onClick={create}
                        disabled={busy || !form.slug || !form.title}
                        className="samoony-pill disabled:opacity-30 sm:col-span-2"
                    >
                        {busy ? "Creating..." : "Create"}
                    </button>
                </div>
            )}

            {items === null ? (
                <p className="text-sm text-black/60">Loading...</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-black/60" data-testid={`${testid}-empty`}>
                    No guides yet. Create one above.
                </p>
            ) : (
                <ul data-testid={`${testid}-list`} className="space-y-2">
                    {items.map((g) => (
                        <li
                            key={g.id}
                            data-testid={`${testid}-row-${g.slug}`}
                            className="border border-black/10 rounded-lg p-3"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <div className="font-display font-bold">
                                        {g.heading || g.title}
                                    </div>
                                    <div className="text-xs text-black/50 font-mono">
                                        /guides/{g.slug} · {g.status} ·{" "}
                                        {g.entry_count} entries
                                        {g.guide_type !== "topic" &&
                                            ` · ${
                                                g.lookup_status === "running"
                                                    ? "lookup running"
                                                    : g.lookup_status === "error"
                                                    ? "lookup failed"
                                                    : g.lookup_at
                                                    ? `ranked ${String(g.lookup_at).slice(0, 10)}`
                                                    : "not ranked"
                                            }`}
                                        {g.feeds_pages > 0 &&
                                            ` · feeds ${g.feeds_pages} creator page${g.feeds_pages === 1 ? "" : "s"}`}
                                    </div>
                                    {g.status !== "published" && g.feeds_pages > 0 && (
                                        <div
                                            data-testid={`${testid}-draft-feeds-${g.slug}`}
                                            className="text-xs text-signal-text mt-1"
                                        >
                                            Draft, but {g.feeds_pages} creator page
                                            {g.feeds_pages === 1 ? " compares" : "s compare"} against
                                            it. Publish it so they show "Included in".
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    {g.url && (
                                        <a
                                            data-testid={`${testid}-view-${g.slug}`}
                                            href={g.url}
                                            target="_blank"
                                            rel="noopener"
                                            className="samoony-pill samoony-pill-ghost"
                                        >
                                            View
                                        </a>
                                    )}
                                    <button
                                        data-testid={`${testid}-edit-${g.slug}`}
                                        onClick={() =>
                                            setEditId(
                                                editId === g.id ? null : g.id
                                            )
                                        }
                                        className="samoony-pill samoony-pill-ghost"
                                    >
                                        {editId === g.id ? "Close" : "Edit"}
                                    </button>
                                </div>
                            </div>
                            {editId === g.id && (
                                <GuideEditor
                                    guideId={g.id}
                                    testid={`${testid}-editor`}
                                    onChanged={async () => {
                                        await load();
                                    }}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

// Editor sub-component: loads one guide, edits its title, heading and intro,
// and publishes / unpublishes. Creator guides are ranked by the lookup;
// staff can rerun it and exclude people. Topic guide entries come from
// creator pages and are shown read-only.
function GuideEditor({ guideId, onChanged, testid }) {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const res = await request(`/admin/guides/${guideId}`);
            setData(res.guide);
            setDirty(false);
        } catch (err) {
            toast.error(err?.message || "Could not load guide.");
        }
    };

    useEffect(() => {
        void refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guideId]);

    const running = data?.lookup_status === "running";

    // Poll while a lookup runs; it takes a few minutes.
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(async () => {
            try {
                const res = await request(`/admin/guides/${guideId}`);
                if (res.guide?.lookup_status !== "running") {
                    setData((prev) => ({
                        ...res.guide,
                        title: dirty ? prev.title : res.guide.title,
                        heading: dirty ? prev.heading : res.guide.heading,
                        intro: dirty ? prev.intro : res.guide.intro,
                    }));
                    if (res.guide?.lookup_status === "error") {
                        toast.error(res.guide.lookup_error || "Lookup failed.");
                    } else {
                        toast.success("Lookup finished.");
                    }
                    onChanged && (await onChanged());
                }
            } catch {
                // Keep polling; a single failed poll is not fatal.
            }
        }, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running, guideId]);

    const edit = (patch) => {
        setData({ ...data, ...patch });
        setDirty(true);
    };

    const saveText = async () => {
        const res = await request(`/admin/guides/${guideId}`, {
            method: "PATCH",
            body: { intro: data.intro, heading: data.heading, title: data.title },
        });
        setData(res.guide);
        setDirty(false);
        return res.guide;
    };

    const save = async () => {
        if (busy || !data) return;
        setBusy(true);
        try {
            await saveText();
            toast.success("Saved.");
            onChanged && (await onChanged());
        } catch (err) {
            toast.error(err?.message || "Could not save.");
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (action) => {
        if (busy) return;
        setBusy(true);
        try {
            // Publishing never drops typed edits: save them first.
            if (dirty) await saveText();
            const res = await request(`/admin/guides/${guideId}/${action}`, {
                method: "POST",
            });
            setData(res.guide);
            toast.success(action === "publish" ? "Guide published." : "Guide unpublished.");
            onChanged && (await onChanged());
        } catch (err) {
            toast.error(err?.message || `Could not ${action}.`);
        } finally {
            setBusy(false);
        }
    };

    // Drafts title, heading and intro from the ranked cards into the fields;
    // nothing is saved until staff press Save.
    const suggestCopy = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await request(`/admin/guides/${guideId}/suggest-copy`, {
                method: "POST",
            });
            edit(res.copy || {});
            toast("Suggested copy added. Review it, then Save.");
        } catch (err) {
            toast.error(err?.message || "Could not suggest copy.");
        } finally {
            setBusy(false);
        }
    };

    const runLookup = async () => {
        if (busy || running) return;
        setBusy(true);
        try {
            await request(`/admin/guides/${guideId}/lookup`, { method: "POST" });
            setData({ ...data, lookup_status: "running" });
            toast("Lookup started. This takes a few minutes.");
        } catch (err) {
            toast.error(err?.message || "Could not start the lookup.");
        } finally {
            setBusy(false);
        }
    };

    // Staff picks: people added by handle. They skip the follower floor and
    // the niche check and are ranked on the next lookup.
    const [seedInput, setSeedInput] = useState("");
    const setSeed = async (handle, add) => {
        if (busy || !handle.trim()) return;
        setBusy(true);
        try {
            const res = await request(`/admin/guides/${guideId}/seed`, {
                method: "POST",
                body: { handle, add },
            });
            setData((prev) => ({
                ...res.guide,
                title: prev.title,
                heading: prev.heading,
                intro: prev.intro,
            }));
            if (add) {
                setSeedInput("");
                toast("Added. Refresh lookup to rank them.");
            }
        } catch (err) {
            toast.error(err?.message || "Could not update.");
        } finally {
            setBusy(false);
        }
    };

    const setExcluded = async (handle, excluded) => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await request(`/admin/guides/${guideId}/exclude`, {
                method: "POST",
                body: { handle, excluded },
            });
            setData((prev) => ({
                ...res.guide,
                title: prev.title,
                heading: prev.heading,
                intro: prev.intro,
            }));
        } catch (err) {
            toast.error(err?.message || "Could not update.");
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <p className="text-sm text-black/60 mt-3">Loading guide...</p>
        );
    }

    const isTopic = data.guide_type === "topic";
    const ranked = !isTopic && Boolean(data.lookup_at);
    const entries = data.entries || [];
    const excluded = data.excluded_handles || [];
    const listed = new Set(entries.map((e) => e.handle));
    const spares = (data.candidates || []).filter(
        (c) => !listed.has(c.handle) && !excluded.includes(c.handle),
    );
    const stats = data.lookup_stats || {};

    return (
        <div className="mt-3 border-t border-black/10 pt-3" data-testid={testid}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <input
                    data-testid={`${testid}-title`}
                    value={data.title || ""}
                    onChange={(e) => edit({ title: e.target.value })}
                    placeholder="Title (SEO)"
                    className="samoony-input"
                />
                <input
                    data-testid={`${testid}-heading`}
                    value={data.heading || ""}
                    onChange={(e) => edit({ heading: e.target.value })}
                    placeholder="Heading (H1)"
                    className="samoony-input"
                />
                <textarea
                    data-testid={`${testid}-intro`}
                    value={data.intro || ""}
                    onChange={(e) => edit({ intro: e.target.value })}
                    placeholder="2 to 3 sentence intro"
                    className="samoony-input sm:col-span-2"
                    rows={3}
                />
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                    data-testid={`${testid}-save`}
                    onClick={save}
                    disabled={busy || !dirty}
                    className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                >
                    Save
                </button>
                {data.status === "published" ? (
                    <button
                        data-testid={`${testid}-unpublish`}
                        onClick={() => setStatus("unpublish")}
                        disabled={busy}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        Unpublish
                    </button>
                ) : (
                    <button
                        data-testid={`${testid}-publish`}
                        onClick={() => setStatus("publish")}
                        disabled={busy || entries.length === 0}
                        className="samoony-pill"
                    >
                        Publish
                    </button>
                )}
                {!isTopic && (
                    <button
                        data-testid={`${testid}-lookup`}
                        onClick={runLookup}
                        disabled={busy || running}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        {running ? "Looking up..." : ranked ? "Refresh lookup" : "Run lookup"}
                    </button>
                )}
                {ranked && (
                    <button
                        data-testid={`${testid}-suggest-copy`}
                        onClick={suggestCopy}
                        disabled={busy || running}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        Suggest copy
                    </button>
                )}
                {dirty && (
                    <span className="font-mono text-[11px] text-black/50">
                        Unsaved changes
                    </span>
                )}
            </div>

            {!isTopic && (
                <p className="text-xs text-black/60 mb-3" data-testid={`${testid}-lookup-status`}>
                    {running
                        ? "Lookup running. This page updates when it finishes."
                        : ranked
                        ? `Last lookup ${String(data.lookup_at).slice(0, 10)}: checked ${stats.probed ?? 0}, passed ${stats.passed ?? 0}, with cards ${stats.carded ?? 0}. Refreshes on its own every 90 days.`
                        : "Hand-written list. Run the lookup to rank it and fill in cards. People listed here are kept as candidates."}
                    {data.lookup_status === "error" && data.lookup_error && (
                        <span className="block text-signal-text">Last run failed: {data.lookup_error}</span>
                    )}
                </p>
            )}

            <div className="samoony-label mb-2">
                {ranked ? `Ranked list (${entries.length})` : `Entries (${entries.length})`}
            </div>
            {entries.length === 0 ? (
                <p className="text-sm text-black/60">None yet.</p>
            ) : (
                <ol className="space-y-2">
                    {entries.map((e, i) => (
                        <GuideEntryRow
                            key={`${e.handle || e.name}-${i}`}
                            e={e}
                            n={i + 1}
                            ranked={ranked}
                            isTopic={isTopic}
                            testid={testid}
                            busy={busy}
                            onExclude={() => setExcluded(e.handle, true)}
                        />
                    ))}
                </ol>
            )}

            {ranked && spares.length > 0 && (
                <div className="mt-4">
                    <div className="samoony-label mb-2">
                        Next in line ({spares.length})
                    </div>
                    <ul className="space-y-1 text-sm">
                        {spares.map((c) => (
                            <li key={c.handle} className="flex items-center justify-between gap-2">
                                <span>
                                    {c.name}{" "}
                                    <span className="font-mono text-xs text-black/60">@{c.handle}</span>
                                </span>
                                <button
                                    onClick={() => setExcluded(c.handle, true)}
                                    disabled={busy}
                                    className="samoony-pill samoony-pill-ghost text-xs"
                                >
                                    Exclude
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {!isTopic && (
                <div className="mt-4" data-testid={`${testid}-seeds`}>
                    <div className="samoony-label mb-2">
                        Add a person ({(data.seed_handles || []).length} added by staff)
                    </div>
                    <div className="flex gap-2">
                        <input
                            data-testid={`${testid}-seed-input`}
                            value={seedInput}
                            onChange={(e) => setSeedInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") setSeed(seedInput, true);
                            }}
                            placeholder="Instagram handle or profile link"
                            className="samoony-input flex-1"
                        />
                        <button
                            data-testid={`${testid}-seed-add`}
                            onClick={() => setSeed(seedInput, true)}
                            disabled={busy || !seedInput.trim()}
                            className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                        >
                            Add
                        </button>
                    </div>
                    {(data.seed_handles || []).length > 0 && (
                        <ul className="space-y-1 text-sm mt-2">
                            {(data.seed_handles || []).map((h) => (
                                <li key={h} className="flex items-center justify-between gap-2">
                                    <span className="font-mono text-xs text-black/60">@{h}</span>
                                    <button
                                        onClick={() => setSeed(h, false)}
                                        disabled={busy}
                                        className="samoony-pill samoony-pill-ghost text-xs"
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="text-xs text-black/50 mt-2">
                        Staff picks are ranked on the next lookup. Refresh lookup after adding.
                    </p>
                </div>
            )}

            {ranked && excluded.length > 0 && (
                <div className="mt-4" data-testid={`${testid}-excluded`}>
                    <div className="samoony-label mb-2">Excluded ({excluded.length})</div>
                    <ul className="space-y-1 text-sm">
                        {excluded.map((h) => (
                            <li key={h} className="flex items-center justify-between gap-2">
                                <span className="font-mono text-xs text-black/60">@{h}</span>
                                <button
                                    onClick={() => setExcluded(h, false)}
                                    disabled={busy}
                                    className="samoony-pill samoony-pill-ghost text-xs"
                                >
                                    Include again
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function GuideEntryRow({ e, n, ranked, isTopic, testid, busy, onExclude }) {
    if (isTopic) {
        return (
            <li className="border border-black/10 rounded-lg p-2 text-sm">
                <div className="font-medium">{e.name}</div>
                <div className="text-xs text-black/60">
                    {e.creator_name || e.creator_samoony_handle}
                    {e.take ? `: ${e.take}` : ""}
                </div>
            </li>
        );
    }
    return (
        <li
            data-testid={`${testid}-entry-${e.handle}`}
            className="border border-black/10 rounded-lg p-2 text-sm"
        >
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-mono text-sm">
                    {ranked && <span className="text-black/40 mr-1">#{n}</span>}
                    {e.name} · @{e.handle}
                    <span className="ml-2 inline-block uppercase tracking-wider text-[10px] text-black/50 border border-black/10 rounded px-1.5 py-0.5">
                        {e.source === "creator" || e.samoony_handle ? "Samoony creator" : e.source === "leader" ? "Leader" : "Manual"}
                    </span>
                </div>
                {ranked && (
                    <button
                        onClick={onExclude}
                        disabled={busy}
                        className="samoony-pill samoony-pill-ghost text-xs"
                        data-testid={`${testid}-exclude-${e.handle}`}
                    >
                        Exclude
                    </button>
                )}
            </div>
            {ranked ? (
                <div className="mt-1 text-xs text-black/60 space-y-0.5">
                    {e.focus && <div>Focus: {e.focus}</div>}
                    {e.where_they_post && <div>Where they post: {e.where_they_post}</div>}
                    {e.known_for && <div>Known for: {e.known_for}</div>}
                </div>
            ) : (
                e.line && <div className="mt-1 text-xs text-black/60">"{e.line}"</div>
            )}
        </li>
    );
}

// Admin-only button that submits every public URL (home, privacy, terms,
// and every approved real creator page) to IndexNow in a single request.
// Shows the HTTP response code so staff can confirm the ping was accepted.
export function IndexNowPingAllButton({ testid = "admin-indexnow-ping-all" }) {
    const { request } = useApi();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async () => {
        setBusy(true);
        setResult(null);
        try {
            const res = await request("/admin/indexnow/ping-all", {
                method: "POST",
                body: {},
            });
            setResult(res || {});
            if (res && res.ok) {
                toast.success(
                    `Pinged ${res.count || 0} URLs (code ${res.status_code}).`
                );
            } else {
                toast.error(
                    `IndexNow responded ${res?.status_code ?? "n/a"} (${res?.reason || "see details"})`
                );
            }
        } catch (e) {
            toast.error(e?.message || "Could not reach IndexNow.");
            setResult({ ok: false, reason: e?.message || "Network error." });
        } finally {
            setBusy(false);
        }
    };
    return (
        <section
            data-testid={testid}
            className="samoony-card mb-6"
        >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="font-display text-base font-bold text-ink">
                        Ping search engines
                    </h2>
                    <p className="text-sm text-ink/60">
                        Submits every public page in the sitemap to IndexNow and Bing.
                    </p>
                </div>
                <button
                    data-testid={`${testid}-button`}
                    onClick={submit}
                    disabled={busy}
                    className="samoony-pill disabled:opacity-30 w-full sm:w-auto"
                >
                    {busy ? "Pinging..." : "Ping search engines (all public pages)"}
                </button>
            </div>
            {result ? (
                <div
                    data-testid={`${testid}-result`}
                    className="mt-3 text-sm font-mono text-ink/80"
                >
                    {typeof result.status_code === "number"
                        ? `IndexNow: ${result.status_code}`
                        : "IndexNow: n/a"}
                    {typeof result.bing_status_code === "number"
                        ? ` · Bing: ${result.bing_status_code}`
                        : " · Bing: not set up"}
                    {typeof result.count === "number"
                        ? ` · submitted ${result.count}`
                        : ""}
                    {result.host ? ` · host ${result.host}` : ""}
                    {result.reason ? ` · ${result.reason}` : ""}
                </div>
            ) : null}
        </section>
    );
}

// Shared pending-ownership tile. Mounts on admin screens, polls `/admin/
// pending-ownership`, hides itself when nobody is waiting. Each row deep
// links back into /admin/creators and auto-opens that creator's panel.
export function PendingOwnershipAdminTile({ testid = "admin-pending-ownership-tile" }) {
    const { request } = useApi();
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let active = true;
        request("/admin/pending-ownership")
            .then((res) => {
                if (!active) return;
                setItems(res?.items || []);
            })
            .catch(() => {})
            .finally(() => active && setLoaded(true));
        return () => { active = false; };
    }, [request]);

    if (!loaded || items.length === 0) return null;

    return (
        <section
            data-testid={testid}
            className="rounded-2xl mb-5 px-5 py-5 sm:px-6 sm:py-6"
            style={{
                background: "var(--color-ink)",
                color: "var(--color-paper)",
            }}
        >
            <div className="flex items-center justify-between gap-3 mb-4">
                <div
                    className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase"
                    style={{ color: "var(--color-paper)", opacity: 0.6 }}
                >
                    Pending ownership
                </div>
                <span
                    data-testid={`${testid}-count`}
                    className="font-mono text-[11px] tracking-[0.12em] uppercase rounded-full px-2 py-0.5"
                    style={{
                        background: "var(--color-signal)",
                        color: "var(--color-ink)",
                    }}
                >
                    {items.length} waiting
                </span>
            </div>
            <ul className="flex flex-col gap-2">
                {items.map((it) => (
                    <li
                        key={it.id}
                        data-testid={`${testid}-row-${it.samoony_handle || it.id}`}
                        className="rounded-xl px-3 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                        style={{ background: "rgba(255,255,255,0.06)" }}
                    >
                        <div className="min-w-0">
                            <div className="font-display text-base truncate">
                                /@{it.samoony_handle || "–"}
                                {it.first_name ? (
                                    <span
                                        className="text-xs font-sans ml-2"
                                        style={{ opacity: 0.6 }}
                                    >
                                        {it.first_name}
                                    </span>
                                ) : null}
                            </div>
                            <div
                                className="font-mono text-[11px] mt-1"
                                style={{ opacity: 0.75 }}
                            >
                                {(it.platforms || []).join(" · ") || "No platforms"}
                                {" · waiting "}
                                {waitingFor(it.created_at) || "–"}
                            </div>
                        </div>
                        <Link
                            data-testid={`${testid}-open-${it.samoony_handle || it.id}`}
                            to={`/admin/creators?open=${encodeURIComponent(it.id)}`}
                            className="samoony-pill samoony-pill-primary shrink-0 text-center"
                        >
                            Open admin tools
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

// Admin-only diagnostic tool. Posts to /admin/source-check with a YouTube
// handle and/or up to 10 TikTok URLs plus the expected TikTok handle. Shows
// the raw helper output on screen. No build, no DB writes.
export function SourceCheckTool({ testid = "admin-source-check-tool" }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const [ytHandle, setYtHandle] = useState("");
    const [tkHandle, setTkHandle] = useState("");
    const [tkUrlsText, setTkUrlsText] = useState("");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        const urls = tkUrlsText
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 10);
        setRunning(true);
        setResult(null);
        try {
            const res = await request("/admin/source-check", {
                method: "POST",
                body: {
                    youtube_handle: ytHandle.trim(),
                    tiktok_handle: tkHandle.trim(),
                    tiktok_video_urls: urls,
                },
            });
            setResult(res);
        } catch (err) {
            toast.error(err?.message || "Source check failed.");
        } finally {
            setRunning(false);
        }
    };

    return (
        <section
            data-testid={testid}
            className="rounded-2xl mb-5 px-5 py-5 sm:px-6 sm:py-6 border"
            style={{
                background: "var(--color-paper)",
                borderColor: "var(--color-divider)",
            }}
        >
            <button
                type="button"
                data-testid={`${testid}-toggle`}
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 text-left"
            >
                <div>
                    <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-ink/60">
                        Admin
                    </div>
                    <div className="font-display text-lg sm:text-xl font-bold text-ink">
                        Source check
                    </div>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink/60">
                    {open ? "Hide" : "Open"}
                </span>
            </button>

            {open && (
                <form
                    onSubmit={submit}
                    data-testid={`${testid}-form`}
                    className="mt-4 flex flex-col gap-3"
                >
                    <label className="flex flex-col gap-1">
                        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60">
                            YouTube handle (optional)
                        </span>
                        <input
                            data-testid={`${testid}-youtube-handle`}
                            type="text"
                            value={ytHandle}
                            onChange={(e) => setYtHandle(e.target.value)}
                            placeholder="@creator"
                            className="rounded-lg border border-divider px-3 py-2 text-sm bg-paper"
                        />
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60">
                            Expected TikTok handle (optional)
                        </span>
                        <input
                            data-testid={`${testid}-tiktok-handle`}
                            type="text"
                            value={tkHandle}
                            onChange={(e) => setTkHandle(e.target.value)}
                            placeholder="@creator"
                            className="rounded-lg border border-divider px-3 py-2 text-sm bg-paper"
                        />
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60">
                            TikTok video URLs (one per line, up to 10)
                        </span>
                        <textarea
                            data-testid={`${testid}-tiktok-urls`}
                            rows={5}
                            value={tkUrlsText}
                            onChange={(e) => setTkUrlsText(e.target.value)}
                            placeholder="https://www.tiktok.com/@creator/video/123..."
                            className="rounded-lg border border-divider px-3 py-2 text-sm font-mono bg-paper"
                        />
                    </label>

                    <div className="flex items-center gap-3">
                        <button
                            type="submit"
                            disabled={running}
                            data-testid={`${testid}-submit`}
                            className="samoony-pill samoony-pill-primary"
                        >
                            {running ? "Checking..." : "Run source check"}
                        </button>
                        {result && (
                            <span
                                data-testid={`${testid}-yt-key-state`}
                                className="font-mono text-[11px] uppercase tracking-[0.12em]"
                                style={{
                                    color: result.youtube_api_key_loaded
                                        ? "var(--color-ok)"
                                        : "var(--color-signal)",
                                }}
                            >
                                YOUTUBE_API_KEY: {result.youtube_api_key_loaded ? "yes" : "no"}
                            </span>
                        )}
                    </div>

                    {result && (
                        <div
                            data-testid={`${testid}-result`}
                            className="mt-2 flex flex-col gap-4"
                        >
                            {result.youtube && (
                                <SourceCheckYouTube
                                    data={result.youtube}
                                    testid={`${testid}-yt-result`}
                                />
                            )}
                            {result.tiktok && (
                                <SourceCheckTiktok
                                    data={result.tiktok}
                                    testid={`${testid}-tk-result`}
                                />
                            )}
                            {!result.youtube && !result.tiktok && (
                                <div className="font-mono text-[12px] text-ink/60">
                                    No inputs provided. Enter a YouTube handle or TikTok URLs and try again.
                                </div>
                            )}
                        </div>
                    )}
                </form>
            )}
        </section>
    );
}

function SourceCheckYouTube({ data, testid }) {
    if (!data) return null;
    if (!data.ok) {
        return (
            <div
                data-testid={testid}
                className="rounded-lg border border-divider px-3 py-3 text-sm"
            >
                <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60 mb-1">
                    YouTube
                </div>
                <div className="text-ink">{data.reason || "Failed."}</div>
            </div>
        );
    }
    return (
        <div
            data-testid={testid}
            className="rounded-lg border border-divider px-3 py-3 text-sm"
        >
            <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60 mb-2">
                YouTube
            </div>
            <div className="flex flex-col gap-1 text-sm">
                <div>
                    <span className="text-ink/60">Channel title:</span>{" "}
                    <span className="font-mono">{data.channel_title || "(empty)"}</span>
                </div>
                <div>
                    <span className="text-ink/60">Channel ID:</span>{" "}
                    <span className="font-mono text-xs">{data.channel_id || "(empty)"}</span>
                </div>
                <div>
                    <span className="text-ink/60">Video count:</span>{" "}
                    <span className="font-mono">{data.video_count}</span>
                </div>
            </div>
            {Array.isArray(data.first_five) && data.first_five.length > 0 && (
                <ul className="mt-3 flex flex-col gap-2">
                    {data.first_five.map((v, i) => (
                        <li
                            key={i}
                            data-testid={`${testid}-video-${i}`}
                            className="text-sm border-t border-divider pt-2 first:border-t-0 first:pt-0"
                        >
                            <div className="font-display">{v.title || "(no title)"}</div>
                            <div className="font-mono text-[11px] text-ink/60">
                                {v.published_at || "(no date)"}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SourceCheckTiktok({ data, testid }) {
    if (!data) return null;
    if (!data.ok) {
        return (
            <div
                data-testid={testid}
                className="rounded-lg border border-divider px-3 py-3 text-sm"
            >
                <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60 mb-1">
                    TikTok
                </div>
                <div className="text-ink">{data.reason || "Failed."}</div>
            </div>
        );
    }
    return (
        <div
            data-testid={testid}
            className="rounded-lg border border-divider px-3 py-3 text-sm"
        >
            <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink/60 mb-2 flex items-center justify-between gap-3">
                <span>TikTok</span>
                <span>
                    matched {data.matched} · skipped {data.skipped}
                </span>
            </div>
            <div className="text-xs text-ink/60 mb-2 font-mono break-all">
                Expected: {data.expected_prefix}
            </div>
            <ul className="flex flex-col gap-2">
                {(data.rows || []).map((r, i) => (
                    <li
                        key={i}
                        data-testid={`${testid}-row-${i}`}
                        className="border-t border-divider pt-2 first:border-t-0 first:pt-0 text-sm"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span
                                className="font-mono text-[11px] uppercase tracking-[0.12em]"
                                style={{
                                    color:
                                        r.status === "matched"
                                            ? "var(--color-ok)"
                                            : "var(--color-signal)",
                                }}
                            >
                                {r.status}
                            </span>
                            <span className="font-mono text-[11px] text-ink/60 truncate max-w-[55%]">
                                {r.url}
                            </span>
                        </div>
                        {r.caption && (
                            <div className="mt-1 text-ink">{r.caption}</div>
                        )}
                        {r.author_url && (
                            <div className="mt-1 font-mono text-[11px] text-ink/60 break-all">
                                {r.author_url}
                            </div>
                        )}
                        {r.reason && r.status === "skipped" && (
                            <div className="mt-1 text-[12px] text-ink/70">
                                {r.reason}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function fmtTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-US", {
            timeZone: "America/Edmonton",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return iso;
    }
}

export default function AdminCreators() {
    const { request } = useApi();
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [creators, setCreators] = useState([]);
    const [query, setQuery] = useState("");
    const [openId, setOpenId] = useState(null);
    const [publicBaseUrl, setPublicBaseUrl] = useState("");
    const [isRealDomain, setIsRealDomain] = useState(false);
    const [searchParams] = useSearchParams();
    const openParam = searchParams.get("open");

    useEffect(() => {
        let active = true;
        request("/me")
            .then((res) => {
                if (!active) return;
                setPublicBaseUrl(res?.public_base_url || "");
                setIsRealDomain(!!res?.is_real_domain);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [request]);

    const load = async () => {
        setLoading(true);
        try {
            const url = query.trim()
                ? `/admin/creators?q=${encodeURIComponent(query.trim())}`
                : "/admin/creators";
            const res = await request(url);
            setCreators(res?.creators || []);
            setForbidden(false);
        } catch (err) {
            if (err?.status === 403) setForbidden(true);
            else toast.error(err?.message || "Could not load creators.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Deep-link: /admin/creators?open={creator_id} auto-expands that row the
    // first time it appears in the list. Used by the pending-ownership tile.
    useEffect(() => {
        if (openParam && creators.some((c) => c.id === openParam)) {
            setOpenId(openParam);
        }
    }, [openParam, creators]);

    const filtered = useMemo(() => creators, [creators]);

    if (forbidden) {
        return (
            <div className="min-h-screen bg-paper text-ink flex items-center justify-center">
                <div
                    data-testid="admin-creators-forbidden"
                    className="text-sm text-black/60"
                >
                    Admins only.
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper text-ink">
            <header className="px-5 sm:px-10 pt-6 pb-4 flex items-center justify-between">
                <Link to="/" className="inline-flex items-center">
                    <Wordmark className="h-6" />
                </Link>
                <Link
                    to="/profile"
                    data-testid="admin-creators-back"
                    className="samoony-pill samoony-pill-ghost"
                >
                    Back to profile
                </Link>
            </header>
            <main
                className="px-5 sm:px-10 pb-16"
                data-testid="admin-creators-page"
            >
                <SourceCheckTool testid="admin-creators-source-check" />
                <PendingOwnershipAdminTile testid="admin-creators-pending-ownership" />
                <IndexNowPingAllButton testid="admin-creators-indexnow" />
                <GuidesAdminTile testid="admin-creators-guides" />
                <h1 className="font-display text-2xl sm:text-3xl font-bold mb-4">
                    Creators
                </h1>
                <div className="flex flex-col gap-2 mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                        <input
                            data-testid="admin-creators-search"
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") void load();
                            }}
                            placeholder="Search handle, name or email"
                            className="samoony-input w-full"
                        />
                        <button
                            data-testid="admin-creators-refresh"
                            onClick={load}
                            className="samoony-pill samoony-pill-ghost w-full"
                        >
                            {loading ? "Loading..." : "Search"}
                        </button>
                    </div>
                </div>

                {!loading && filtered.length === 0 && (
                    <div
                        className="text-sm text-black/50"
                        data-testid="admin-creators-empty"
                    >
                        No creators found.
                    </div>
                )}

                {filtered.length > 0 && (
                    <ul
                        data-testid="admin-creators-list"
                        className="bg-white border border-black/10 rounded-2xl divide-y divide-black/10"
                    >
                        {filtered.map((c) => (
                            <CreatorRow
                                key={c.id}
                                c={c}
                                open={openId === c.id}
                                onToggle={() =>
                                    setOpenId(openId === c.id ? null : c.id)
                                }
                                onArchive={async (row) => {
                                    if (
                                        !window.confirm(
                                            `Archive /@${row.samoony_handle}? This cannot be rebuilt.`,
                                        )
                                    )
                                        return;
                                    try {
                                        await request(
                                            `/admin/creators/${row.id}/archive`,
                                            { method: "POST" },
                                        );
                                        toast.success("Archived.");
                                        await load();
                                    } catch (err) {
                                        toast.error(
                                            err?.message ||
                                                "Could not archive.",
                                        );
                                    }
                                }}
                                publicBaseUrl={publicBaseUrl}
                                isRealDomain={isRealDomain}
                            />
                        ))}
                    </ul>
                )}
            </main>
        </div>
    );
}

function CreatorRow({ c, open, onToggle, onArchive, publicBaseUrl, isRealDomain }) {
    const livePageHref =
        c.live_page_path
            ? isRealDomain && publicBaseUrl
                ? `${publicBaseUrl}/@${c.samoony_handle}`
                : c.live_page_path
            : null;
    const status = STATUS_LABEL[c.page_status] || c.page_status || "Draft";
    const panelCreator = {
        id: c.id,
        first_name: c.first_name,
        display_name: c.display_name,
        samoony_handle: c.samoony_handle,
        is_demo: false,
        approved_at: c.approved_at,
        ownership_status: c.ownership_status,
        handles: {
            instagram: c.instagram_handle || null,
            tiktok: c.tiktok_handle || null,
            youtube: c.youtube_handle || null,
        },
        tiktok_video_urls: c.tiktok_video_urls || [],
        sources_updated_at: c.sources_updated_at || null,
    };
    const panelHref =
        isRealDomain && publicBaseUrl
            ? `${publicBaseUrl}/@${c.samoony_handle}`
            : `/@${c.samoony_handle}`;
    return (
        <li
            className="px-4 py-4"
            data-testid={`admin-creators-row-${c.samoony_handle}`}
        >
            <div className="flex flex-col gap-3">
                <div className="min-w-0 w-full">
                    <div className="font-display text-base text-ink truncate">
                        {c.first_name || c.display_name || "Creator"}{" "}
                        <span className="text-xs text-black/50 font-sans">
                            /@{c.samoony_handle}
                        </span>
                    </div>
                    <div className="font-mono text-[11px] text-black/60 break-all">
                        IG @{c.instagram_handle || "–"}
                        {c.tiktok_handle ? ` · TT @${c.tiktok_handle}` : ""}
                        {c.youtube_handle ? ` · YT @${c.youtube_handle}` : ""}
                        {" · "}{status}
                        {c.held_count ? ` · ${c.held_count} held` : ""} · last
                        build {fmtTime(c.last_build_at) || "–"}
                        {c.email ? ` · ${c.email}` : ""}
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                    <button
                        data-testid={`admin-creators-open-${c.samoony_handle}`}
                        onClick={onToggle}
                        className="samoony-pill samoony-pill-primary w-full"
                    >
                        {open ? "Hide tools" : "Open tools"}
                    </button>
                    {livePageHref ? (
                        <a
                            href={livePageHref}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-creators-live-${c.samoony_handle}`}
                            className="samoony-pill samoony-pill-ghost text-center w-full"
                        >
                            Live page
                        </a>
                    ) : (
                        <span
                            data-testid={`admin-creators-not-live-${c.samoony_handle}`}
                            className="samoony-pill samoony-pill-ghost text-center w-full opacity-50 cursor-default"
                        >
                            Not live yet
                        </span>
                    )}
                    {c.latest_draft_path ? (
                        <a
                            href={c.latest_draft_path}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-creators-draft-${c.samoony_handle}`}
                            className="samoony-pill samoony-pill-ghost text-center w-full"
                        >
                            Latest draft
                            {c.latest_draft_version
                                ? ` v${c.latest_draft_version}`
                                : ""}
                        </a>
                    ) : null}
                    {c.archivable ? (
                        <button
                            data-testid={`admin-creators-archive-${c.samoony_handle}`}
                            onClick={() => onArchive && onArchive(c)}
                            className="samoony-pill samoony-pill-ghost w-full"
                        >
                            Archive
                        </button>
                    ) : null}
                </div>
            </div>
            {open && (
                <div
                    className="mt-4"
                    data-testid={`admin-creators-panel-${c.samoony_handle}`}
                >
                    <AdminCreatorPanel
                        creator={panelCreator}
                        publicPageHref={panelHref}
                    />
                </div>
            )}
        </li>
    );
}
